import {
  TextField,
  TextInput,
  BooleanInput,
  required,
  WithRecord,
} from "react-admin";
import { RecordSummary, MyShow } from "../../ra-lb-tools/components/MyShow";
import BusinessIcon from "@mui/icons-material/Business";
import {
  MyReferenceField,
  MyReferenceInput,
} from "../../ra-lb-tools/components/myReference";
import { MyList } from "../../ra-lb-tools/components/myList";
import {
  MyEdit as Edit,
  MyCreate as Create,
} from "../../ra-lb-tools/components/myMutator";
import { LabeledColumn } from "../../ra-lb-tools/components/labeledColumn";
import { UserField, UserInput } from "../users";
import {
  PlanSubscriptionField,
  PlanSubscriptionList,
} from "./plan_subsciptions";
import { useParams } from "react-router-dom";
import { MyBooleanField as BooleanField } from "../../components/myBooleanField";
import {
  MyTabbedShowLayout,
  Tab,
} from "../../ra-lb-tools/components/myTabbedShowLayout";
import { LockerUsageRecordsList } from "./lockerUsageRecord";
import { MyDateField } from "../../ra-lb-tools/components/myDateField";
import { InvoiceList, OrganizationInvoiceShow } from "./organization_invoices";
import { Box } from "@mui/material";
import { Debug } from "../../ra-lb-tools/components/debug";
import { StripeLink } from "./stripeLink";
import { OrganizationMemberList } from "./organizationMembers";
import get from "lodash/get";
import { KeycloakLink } from "../misc/keycloakLink";
import { TowerList } from "../tower/towers";
import { NullableBooleanField } from "../../ra-lb-tools/components/nullableBooleanField";
import { ClientsList } from "../clients";
import { getValidResource } from "../../ra-lb-tools/acl/tools";
import {
  EditOrganizationSettingsButton,
  OrganizationSettingsField,
} from "./organizationSettings";

export const OrganizationIcon = BusinessIcon;

const filters = () => [
  <TextInput source="name" alwaysOn key="name" />,
  <NullableBooleanField source="active" key={"active"} alwaysOn />,
];

export const OrganizationList = (props) => (
  <MyList resource="organizations" filters={filters()} {...props}>
    <TextField source="id" />
    <TextField source="name" />
    <BooleanField source="active" />
    <UserField source="accountOwnerId" />
    <UserField source="serviceAccountId" />
    <Box source="stripe">
      <TextField source="customerId" />
      <Debug>
        <StripeLink id="customerId" />
      </Debug>
    </Box>
    <PlanSubscriptionField source="planSubscriptionId" />
    <TextField source="phone" />
    <TextField source="billingAddress" />
    <TextField source="shippingAddress" />
    <MyDateField source="createdAt" showTime />
    <MyDateField source="updatedAt" showTime />
    <Box source="keycloak">
      <TextField source="externalId" />
      <Debug>
        <KeycloakLink id="externalId" entity={"organizations"} />
      </Debug>
    </Box>
    <TextField source="alias" />
    <TextField source="domain" />
  </MyList>
);

export const OrganizationEdit = (props) => {
  return (
    <Edit {...props}>
      <TextInput source="name" validate={required()} />
      <WithRecord
        render={(org) => (
          <UserInput
            source="accountOwnerId"
            disabled
            getOptionDisabled={(user) =>
              !!get(user, "organizationOwned") &&
              get(user, "organizationOwned") != org.id
            }
          />
        )}
      />
      <WithRecord
        render={(org) => (
          <UserInput
            disabled
            source="serviceAccountId"
            getOptionDisabled={(user) =>
              !!get(user, "organizationServiced") &&
              get(user, "organizationServiced") != org.id
            }
          />
        )}
      />

      <TextInput source="phone" />
      <TextInput source="billingAddress" />
      <TextInput source="shippingAddress" />
      <BooleanInput source="active" />
    </Edit>
  );
};

export const OrganizationCreate = (props) => {
  const disabled = (user) =>
    Boolean(get(user, "organizationOwned")) ||
    Boolean(get(user, "organizationServiced"));
  const render = (user, label) => {
    if (disabled(user)) {
      const detail = get(user, "organizationOwned") ? "owns" : "services";
      return (
        <>
          {label}
          <Box sx={{ marginLeft: 5 }}>({detail})</Box>
        </>
      );
    } else {
      return label;
    }
  };

  return (
    <Create {...props}>
      <TextInput source="name" validate={required()} />
      <UserInput
        source="accountOwnerId"
        validate={required()}
        getOptionDisabled={disabled}
        render={render}
      />
      <UserInput
        source="serviceAccountId"
        getOptionDisabled={disabled}
        render={render}
        helperText={
          <Box>
            If service account is left empty the system will create the service
            account and app client for you (preferred solution)
          </Box>
        }
      />
      <TextInput source="phone" />
      <TextInput source="billingAddress" />
      <TextInput source="shippingAddress" />
      <BooleanInput source="active" />
    </Create>
  );
};

export const OrganizationShow = ({ ...props }) => {
  const paramId = useParams()["id"];
  const id = parseInt(props.id || paramId);
  const tabOrgFilter = { organizationId: id };

  const org_resource = getValidResource("organization");

  return (
    <MyShow {...props} extraActions={<EditOrganizationSettingsButton />}>
      <MyTabbedShowLayout>
        <Tab label="Summary" can={true}>
          <RecordSummary>
            <LabeledColumn>
              <TextField source="id" />
              <TextField source="name" />
              <BooleanField source="active" />
              <UserField source="accountOwnerId" />
              <UserField source="serviceAccountId" />
              <PlanSubscriptionField source="planSubscriptionId" />
            </LabeledColumn>
            <LabeledColumn>
              <TextField source="alias" />
              <TextField source="domain" />
              <Box source="stripe">
                <TextField source="customerId" />
                <Debug>
                  <StripeLink id="customerId" />
                </Debug>
              </Box>
              <Box source="keycloak">
                <TextField source="externalId" />
                <Debug>
                  <KeycloakLink id="externalId" entity={"organizations"} />
                </Debug>
              </Box>
              <TextField source="phone" />
              <TextField source="billingAddress" />
              <TextField source="shippingAddress" />
            </LabeledColumn>
            <LabeledColumn>
              <MyDateField source="createdAt" showTime />
              <MyDateField source="updatedAt" showTime />
              <OrganizationSettingsField
                source="__organization_settings"
                label="Organization Settings"
                can={true}
              />
            </LabeledColumn>
          </RecordSummary>
        </Tab>
        <Tab
          label="Subscriptions"
          path="Subscriptions"
          can={{ action: "list", resource: "plan-subscriptions" }}
        >
          <PlanSubscriptionList filter={tabOrgFilter} />
        </Tab>
        <Tab
          label="Locker Usage Records"
          path="locker-usage-records"
          can={{ action: "list", resource: "locker-usage-records" }}
        >
          <LockerUsageRecordsList filter={tabOrgFilter} />
        </Tab>
        <Tab
          label="Upcoming Invoice"
          path="upcoming-invoice"
          can={{ action: "read_invoice", resource: org_resource }}
        >
          <OrganizationInvoiceShow id={props.id} />
        </Tab>
        <Tab
          label="Invoices"
          path="invoices"
          can={{ action: "query_invoices", resource: org_resource }}
        >
          <InvoiceList id={props.id} />
        </Tab>
        <Tab
          label="Members"
          path="members"
          can={{ action: "read_members", resource: org_resource }}
        >
          <OrganizationMemberList />
        </Tab>
        <Tab
          label="Towers"
          path="towers"
          can={{ action: "list", resource: "towers" }}
        >
          <WithRecord
            render={(org) => (
              <TowerList
                filter={{ ownerId: [org.accountOwnerId, org.serviceAccountId] }}
              />
            )}
          />
        </Tab>
        <Tab
          label="Clients"
          path="clients"
          can={{ action: "list", resource: "clients" }}
        >
          <ClientsList filter={tabOrgFilter} />
        </Tab>
      </MyTabbedShowLayout>
    </MyShow>
  );
};

export const OrganizationInput = (props) => {
  return (
    <MyReferenceInput
      source="organizationId"
      reference="organizations"
      {...props}
    />
  );
};

export const OrganizationField = (props) => {
  return (
    <MyReferenceField
      source="organizationId"
      reference="organizations"
      {...props}
    />
  );
};
